import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api/client";
import type {
  Attachment,
  CatalogueService,
  PersonRef,
  SavedFilter,
  GroupOption,
  TicketDetail,
  TicketPage,
  TimelineItem,
} from "../api/types";

const TICKETS = ["tickets"] as const;
const ticketPath = (ref: string) => `/api/tickets/${encodeURIComponent(ref)}`;

/** Query string from a flat object; arrays repeat the key (status=a&status=b). */
export function toQuery(
  params: Record<string, string | number | boolean | string[] | number[] | null | undefined>,
): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === "" || value === false) continue;
    if (Array.isArray(value)) value.forEach((v) => search.append(key, String(v)));
    else search.set(key, String(value));
  }
  return search.toString();
}

export interface TicketFilters {
  /** mine: raised or copied in on. assigned: assigned to me or a group I'm in. workspace: staff. */
  scope: "mine" | "assigned" | "workspace";
  queue?: "all" | "unassigned" | "my_team" | "mine" | "overdue" | undefined;
  status?: string[] | undefined;
  priority?: number[] | undefined;
  service_id?: string | undefined;
  group_id?: string | undefined;
  owner_id?: string | undefined;
  type?: string[] | undefined;
  created_from?: string | undefined;
  updated_from?: string | undefined;
  no_owner?: boolean | undefined;
  overdue?: boolean | undefined;
  tag?: string | undefined;
  q?: string | undefined;
  sort?: "priority" | "updated" | "created" | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
}

export function useTickets(filters: TicketFilters) {
  return useQuery({
    queryKey: [...TICKETS, "list", filters],
    queryFn: ({ signal }) => api<TicketPage>(`/api/tickets?${toQuery({ ...filters })}`, { signal }),
    placeholderData: keepPreviousData,
  });
}

/** I've seen this ticket's conversation up to now. */
export function useMarkTicketRead() {
  return useMutation({
    mutationFn: (reference: string) => api<undefined>(`${ticketPath(reference)}/read`, { method: "POST" }),
  });
}

export function useTicket(ref: string) {
  return useQuery({
    queryKey: [...TICKETS, "detail", ref],
    queryFn: ({ signal }) => api<TicketDetail>(ticketPath(ref), { signal }),
  });
}

export function useTimeline(ref: string) {
  return useQuery({
    queryKey: [...TICKETS, "timeline", ref],
    queryFn: ({ signal }) => api<TimelineItem[]>(`${ticketPath(ref)}/timeline`, { signal }),
  });
}

export function useCatalogue() {
  return useQuery({
    queryKey: ["catalogue"],
    queryFn: ({ signal }) => api<CatalogueService[]>("/api/catalogue", { signal }),
    staleTime: 5 * 60_000,
    // The catalogue carries each service's status, so keep it fresh during an incident.
    refetchInterval: 5 * 60_000,
  });
}

/** After any change: show the returned ticket at once, then refresh its timeline and lists. */
function useTicketRefresh(ref: string) {
  const queryClient = useQueryClient();
  return async (ticket?: TicketDetail) => {
    if (ticket) queryClient.setQueryData([...TICKETS, "detail", ref], ticket);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [...TICKETS, "timeline", ref] }),
      queryClient.invalidateQueries({ queryKey: [...TICKETS, "list"] }),
      ticket ? Promise.resolve() : queryClient.invalidateQueries({ queryKey: [...TICKETS, "detail", ref] }),
    ]);
  };
}

/** A versioned change: POST /api/tickets/{ref}/{action} with the ticket's current version. */
export function useTicketAction<TBody extends object>(ref: string, action: string) {
  const refresh = useTicketRefresh(ref);
  return useMutation({
    mutationFn: ({ version, ...body }: TBody & { version: number }) =>
      api<TicketDetail>(`${ticketPath(ref)}/${action}`, { method: "POST", body: { version, ...body } }),
    onSuccess: refresh,
  });
}

export function useAddWatcher(ref: string) {
  const refresh = useTicketRefresh(ref);
  return useMutation({
    mutationFn: (person: PersonChoice) =>
      api<TicketDetail>(`${ticketPath(ref)}/watchers`, { method: "POST", body: person }),
    onSuccess: refresh,
  });
}

export function useRemoveWatcher(ref: string) {
  const refresh = useTicketRefresh(ref);
  return useMutation({
    mutationFn: (userId: string) =>
      api<TicketDetail | undefined>(`${ticketPath(ref)}/watchers/${encodeURIComponent(userId)}`, { method: "DELETE" }),
    onSuccess: (ticket) => refresh(ticket ?? undefined),
  });
}

export function uploadFiles(ref: string, files: File[], commentId?: string): Promise<Attachment[]> {
  const form = new FormData();
  files.forEach((file) => form.append("files", file));
  if (commentId) form.append("comment_id", commentId);
  return api<Attachment[]>(`${ticketPath(ref)}/attachments`, { method: "POST", body: form });
}

export interface CommentInput {
  body: string;
  isInternal: boolean;
  mentions: string[];
  files: File[];
}

/** Post a message, then its files. If the files fail, the message is still saved. */
export function useAddComment(ref: string) {
  const refresh = useTicketRefresh(ref);
  return useMutation({
    mutationFn: async ({ body, isInternal, mentions, files }: CommentInput) => {
      const comment = await api<TimelineItem>(`${ticketPath(ref)}/comments`, {
        method: "POST",
        body: { body, is_internal: isInternal, mentions },
      });
      if (files.length) await uploadFiles(ref, files, comment.id.replace(/^comment-/, ""));
      return comment;
    },
    onSettled: () => refresh(),
  });
}

export interface NewTicketInput {
  type: string;
  title: string;
  description: string;
  service_id: string | null;
  category_id: string | null;
  impact: string;
  urgency: string;
  is_restricted: boolean;
  client_request_id: string;
  app_context?: Record<string, string> | undefined;
  /** Staff only: raise it for someone else (DECISIONS #44). */
  on_behalf_of?: PersonChoice | undefined;
  /** Colleagues affected too, who share the ticket (DECISIONS #49). */
  shared_with?: PersonChoice[] | undefined;
}

export interface PersonChoice {
  user_id: string | null;
  object_id: string | null;
  email: string;
  name: string;
}

export interface CreatedTicket {
  ticket: TicketDetail;
  failedUpload: string | null;
}

export function useCreateTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ input, files }: { input: NewTicketInput; files: File[] }): Promise<CreatedTicket> => {
      const ticket = await api<TicketDetail>("/api/tickets", { method: "POST", body: input });
      let failedUpload: string | null = null;
      if (files.length) {
        try {
          await uploadFiles(ticket.reference, files);
        } catch (error) {
          failedUpload = error instanceof Error ? error.message : "Your files couldn't be attached.";
        }
      }
      return { ticket, failedUpload };
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: TICKETS }),
  });
}

// --- Saved filters -------------------------------------------------------------------------

export function useSavedFilters() {
  return useQuery({
    queryKey: ["saved-filters"],
    queryFn: ({ signal }) => api<SavedFilter[]>("/api/saved-filters", { signal }),
  });
}

export function useSaveFilter() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; filters: Record<string, unknown> }) =>
      api<SavedFilter>("/api/saved-filters", { method: "POST", body: input }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["saved-filters"] }),
  });
}

export function useDeleteFilter() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<undefined>(`/api/saved-filters/${encodeURIComponent(id)}`, { method: "DELETE" }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["saved-filters"] }),
  });
}

// --- Directory -----------------------------------------------------------------------------

/** Groups a ticket can be assigned to. */
export function useGroupOptions(enabled = true) {
  return useQuery({
    queryKey: ["directory", "groups"],
    queryFn: ({ signal }) => api<GroupOption[]>("/api/directory/groups", { signal }),
    enabled,
    staleTime: 60_000,
  });
}

/** Create a group with its members (from the assign dialog, or Admin → Groups). */
export function useCreateGroup() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; members: PersonChoice[] }) =>
      api<GroupOption>("/api/directory/groups", { method: "POST", body: input }),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ["directory", "groups"] }),
        queryClient.invalidateQueries({ queryKey: ["admin", "groups"] }),
      ]),
  });
}

export function usePeopleSearch(q: string, kind: "people" | "staff" = "people") {
  const minimum = kind === "people" ? 2 : 0;
  return useQuery({
    queryKey: ["directory", kind, q],
    queryFn: ({ signal }) => api<PersonRef[]>(`/api/directory/${kind}?${toQuery({ q })}`, { signal }),
    enabled: q.trim().length >= minimum,
    placeholderData: keepPreviousData,
  });
}

export function attachmentUrl(ref: string, attachmentId: string): string {
  return `${ticketPath(ref)}/attachments/${encodeURIComponent(attachmentId)}`;
}
